import * as React from 'react'
import { useFeatureValue } from 'growthbook'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { useCurrentCurrency } from '@repo/cf-user/hooks/use-current-currency'
import { localStore } from 'utils/storage'

import {
  AUTO_OPEN_DELAY,
  PROMO_POPUPS_CONFIG,
  STUDIO_AI_TRIAL_POPUP_EXPERIMENT_FLAG,
} from '../config'
import {
  getImageProps,
  getPopupTexts,
  injectBasillionFont,
  shouldShowPopup,
  removeBasillionFont,
} from '../utils'
import { Modal } from '../modal'
import { useHeaderContext, useTranslations } from '../../../contexts/header-config-context'

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export function PromoPopupLoggedInUnsubscribed({ isOpen, setIsOpen }: Props) {
  const { isHomepage } = useHeaderContext()

  React.useEffect(() => {
    injectBasillionFont()

    const timeout = setTimeout(() => {
      if (shouldShowPopup('LOGGED_IN_UNSUBSCRIBED')) {
        setIsOpen(true)
        sendAmplitudeEvent(AmplitudeEvents.PROMO_POPUP_VIEWED, {
          type: 'Logged In Unsubscribed',
        })
      }
    }, AUTO_OPEN_DELAY)

    return () => {
      clearTimeout(timeout)
      removeBasillionFont()
    }
  }, [setIsOpen])

  // GROW-2967: the Studio AI trial experiment runs on the homepage only. Off the homepage we
  // render the control popup and never evaluate the flag, so no exposure is logged elsewhere.
  return isHomepage ? (
    <ExperimentPopup isOpen={isOpen} setIsOpen={setIsOpen} />
  ) : (
    <ControlPopup isOpen={isOpen} setIsOpen={setIsOpen} />
  )
}

function ExperimentPopup({ isOpen, setIsOpen }: Props) {
  const t = useTranslations()
  const currency = useCurrentCurrency()
  const experimentVariant = useFeatureValue(STUDIO_AI_TRIAL_POPUP_EXPERIMENT_FLAG, 'control')

  if (experimentVariant !== 'variant-1' && experimentVariant !== 'variant-2') {
    return <ControlPopup isOpen={isOpen} setIsOpen={setIsOpen} />
  }

  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.code ?? 'usd',
  }).format(0)
  const variantKey = experimentVariant === 'variant-2' ? 'v2' : 'v1'

  return (
    <UnsubscribedModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t(`popups.${variantKey}_title`)}
      description={t(`popups.${variantKey}_description`)}
      buttonText={t(`popups.${variantKey}_cta`)}
      microcopy={t(`popups.${variantKey}_microcopy`).replace('#zeroPrice#', zeroPrice)}
      hideImage
      subscriptionType="studio-ai"
    />
  )
}

function ControlPopup({ isOpen, setIsOpen }: Props) {
  const t = useTranslations()
  const { title, description, buttonText } = getPopupTexts({
    promoPopupType: 'LOGGED_IN_UNSUBSCRIBED',
    t,
  })

  return (
    <UnsubscribedModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title}
      description={description}
      buttonText={buttonText}
    />
  )
}

function UnsubscribedModal({
  isOpen,
  setIsOpen,
  title,
  description,
  buttonText,
  microcopy,
  hideImage = false,
  subscriptionType,
}: Props & {
  title: string
  description: string
  buttonText: string
  microcopy?: string
  hideImage?: boolean
  subscriptionType?: 'studio-ai'
}) {
  return (
    <Modal
      backgroundColor="bg-[#00CCCC]"
      title={title}
      description={description}
      buttonText={buttonText}
      microcopy={microcopy}
      hideImage={hideImage}
      subscriptionType={subscriptionType}
      imageProps={getImageProps('LOGGED_IN_UNSUBSCRIBED', 1)}
      promoPopupType="LOGGED_IN_UNSUBSCRIBED"
      // oxlint-disable-next-line require-await
      onClick={async () => {
        if (typeof window !== 'undefined') {
          localStore.setString(
            PROMO_POPUPS_CONFIG.LOGGED_IN_UNSUBSCRIBED.CLOSE_KEY,
            Date.now().toString(),
          )
        }
      }}
      onClose={() => {
        sendAmplitudeEvent(AmplitudeEvents.PROMO_POPUP_CLOSED, {
          type: 'logged-in-unsubscribed',
        })
      }}
      isProcessing={false}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  )
}
